import React, { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import clientAxiosInstance from "../api/clientAxiosInstance";
import { formatAccountName } from "../utils/formatters";

function LinkBankAccountModal({ 
  isOpen, 
  onClose, 
  household, 
  bankAccountId, 
  bankAccountName,
  onSuccess, 
  clientToken = null,
  preSelectedAccountId = null
}) {
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Set preselected account when modal opens
  useEffect(() => {
    if (isOpen && preSelectedAccountId) {
      setSelectedAccounts([preSelectedAccountId]);
    }
  }, [isOpen, preSelectedAccountId]);

  const handleAccountToggle = (accountId) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      }
      return [...prev, accountId];
    });
    setError("");
  };

  const handleSelectAll = () => {
    const allAccountIds = (household?.accounts || []).map(acc => acc.id);
    if (selectedAccounts.length === allAccountIds.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(allAccountIds);
    }
  };

  const handleSave = async () => {
    if (selectedAccounts.length === 0) {
      setError("Please select at least one account to link");
      return;
    }

    setSaving(true);
    setError("");

    try {
      let response;
      if (clientToken) {
        const clientPayload = {
          accounts: {}
        };

        selectedAccounts.forEach(accountId => {
          clientPayload.accounts[accountId] = {
            bank_accounts: { link: [bankAccountId] }
          };
        });

        response = await clientAxiosInstance.put(
          `/client/household/${household.id}`,
          clientPayload,
          {
            headers: {
              Authorization: `Bearer ${clientToken}`
            }
          }
        );
      } else {
        const payload = {
          accounts: selectedAccounts.map(accountId => ({
            account_id: accountId,
            update: {
              add_bank_accounts: [bankAccountId]
            }
          }))
        };

        response = await axiosInstance.put(
          `/households/${household.id}/accounts/bulk`,
          payload
        );
      }

      handleClose();
      if (onSuccess) {
        onSuccess(response?.data || null);
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(", "));
      } else {
        setError("Failed to link bank account. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedAccounts([]);
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  const accounts = household?.accounts || [];
  const allSelected = accounts.length > 0 && selectedAccounts.length === accounts.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Link Bank Account</h2>
              <p className="text-sm text-gray-500 mt-1">
                Link "{bankAccountName}" to brokerage accounts
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={saving}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <p className="text-gray-600">
            Select the brokerage accounts you want to link with this bank account. This bank account will be used for money movements (deposits/withdrawals) for the selected accounts.
          </p>

          {accounts.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="text-gray-600">No brokerage accounts found for this household.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700">
                  {selectedAccounts.length} of {accounts.length} accounts selected
                </span>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  disabled={saving}
                >
                  {allSelected ? "Deselect All" : "Select All"}
                </button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {accounts.map((account, index) => {
                  const isSelected = selectedAccounts.includes(account.id);
                  const accountLabel = formatAccountName(account, index + 1, household?.members || []);

                  return (
                    <label
                      key={account.id}
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                      } ${saving ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      <span className="relative flex shrink-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleAccountToggle(account.id)}
                          className="sr-only peer"
                          disabled={saving}
                        />
                        <span
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                            isSelected
                              ? "bg-emerald-600 border-emerald-600"
                              : "border-gray-300 bg-white hover:border-emerald-400"
                          }`}
                        >
                          {isSelected && (
                            <svg className="w-3.5 h-3.5 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                      </span>
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{accountLabel}</span>
                        {account.custodian && (
                          <span className="block text-sm text-gray-500">{account.custodian}</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={saving}
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              selectedAccounts.length > 0 ? `Link to ${selectedAccounts.length} Account${selectedAccounts.length > 1 ? 's' : ''}` : "Done"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LinkBankAccountModal;
